/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Layout from "../../Components/Layout";
import AddItemButton from "../../Components/AddItemButton";
import { useNavigate } from "react-router-dom";
import { usePagePermissions } from "../../Components/PagePermission";
import { Box, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import DeleteModal from "../../Modals/DeleteModal";
import { BASE_API_URL } from "../../API/Base";
import AxiosInstance from "../../API/axios";
import toast from "react-hot-toast";
import { Delete, EditNote, MenuBook } from "@mui/icons-material";
import { Courses, WrapTextCellProps } from "../../types";
import theme from "../../theme";

const Course = () => {
  const [rows, setRows] = useState<Courses[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const navigate = useNavigate();

  const permissions = usePagePermissions(9);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue]);

  const fetchData = async (
    page: number,
    pageSize: number,
    searchValue: string
  ) => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get(
        `${BASE_API_URL}/admin/courses/v1/get-all-courses-with-sections-and-lessons`,
        {
          params: {
            page,
            per_page: pageSize,
            search_query: searchValue,
          },
        }
      );

      if (
        !response.data ||
        !response.data.courses_with_sections_and_lessons ||
        response.data.total_count == null
      ) {
        console.error("Invalid response format:", response.data);
        setLoading(false);
        return;
      }

      const formattedData = response.data.courses_with_sections_and_lessons.map(
        (row: Courses, index: number) => ({
          ...row,
          row: row.Course_Id,
          actionsID: startIndex + index + 1,
        })
      );

      setRows(formattedData);
      setRowCount(response.data.total_count);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, debouncedSearchValue);
  }, [currentPage, pageSize, debouncedSearchValue]);

  // Calculate start and end index for the visible rows
  const startIndex = (currentPage - 1) * pageSize;

  const base64Encode = (str: string) => {
    return btoa(str);
  };
  const base64Decode = (encodedStr: string) => {
    return atob(encodedStr);
  };

  // Component to wrap text cells and enable white-space wrapping
  const WrapTextCell: React.FC<WrapTextCellProps> = ({ value }) => {
    return <div style={{ whiteSpace: "pre-wrap" }}>{value}</div>;
  };

  const handleEdit = (row: any) => {
    const encodedId = base64Encode(row.course_id);
    navigate(`/courses/manage/form/edit/${encodedId}`);
  };

  const handleView = (row: any) => {
    const encodedId = base64Encode(row.course_id);
    navigate(`/courses/manage/form/view/${encodedId}`);
  };

  const handleDelete = (id: string) => {
    const encodedId = base64Encode(id);
    setIsDeleteModalOpen(true);

    setSelectedRowId(encodedId);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const actionColumn =
    permissions?.Update || permissions?.Delete || permissions?.View
      ? {
          field: "action",
          headerName: "Action",
          width: 120,
          headerClassName:
            "bg-primary text-white font-bold flex flex-row items-center",
          renderCell: (params: any) => (
            <div className="flex flex-row gap-x-1">
              {permissions?.Update && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Edit cursor-pointer"
                  onClick={() => {
                    handleEdit(params.row);
                  }}
                >
                  <Tooltip title="Edit">
                    <EditNote sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.Delete && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Delete cursor-pointer"
                  onClick={() => {
                    handleDelete(params.row.Id);
                    const actionId = permissions?.Delete?.id;
                  }}
                >
                  <Tooltip title="Delete">
                    <Delete sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.View && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-View cursor-pointer flex flex-row items-center justify-center"
                  onClick={() => {
                    handleView(params.row);
                    const actionId = permissions?.View?.id;
                  }}
                >
                  <Tooltip title="View">
                    <MenuBook sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
            </div>
          ),
        }
      : undefined;

  const handleDeleteConfirm = async () => {
    try {
      const decodedId = selectedRowId ? base64Decode(selectedRowId) : null;

      const response = await AxiosInstance.delete(
        `${BASE_API_URL}/admin/courses/v1/delete-course/${decodedId}`
      );

      if (response.data) {
        setIsDeleteModalOpen(false);
        setRows((prevRows) =>
          prevRows.filter((row: any) => row.course_id.toString() !== decodedId)
        );
        toast.success("Data deleted successfully");
      } else {
        toast.error("Error deleting data");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      setIsDeleteModalOpen(false);
    }
  };

  const coloumns: GridColDef[] = [
    {
      field: "actionsID",
      headerName: "ID",
      width: 70,
      renderCell: (params) => (
        <div className="cellWithStatus">{params.value}</div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },
    ...(actionColumn ? [actionColumn] : []),
    {
      field: "title",
      headerName: "Course name",
      cellClassName: "capitalize",
      width: 190,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },

    {
      field: "price",
      headerName: "Course price",

      width: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },
    {
      field: "category_name",
      headerName: "Category",
      cellClassName: "capitalize",

      minWidth: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },

    {
      field: "short_description",
      headerName: "Short Description",
      cellClassName: "capitalize",
      minWidth: 300,
      renderCell: (params) => (
        <div className="whitespace-pre-wrap">
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },

    {
      field: "outcome",
      headerName: "Course outcome",
      cellClassName: "capitalize wrap",
      minWidth: 300,
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
      renderCell: (params) => (
        <div className="">
          {" "}
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
    {
      field: "requirements",
      headerName: "Course requirements",
      cellClassName: "capitalize wrap",
      minWidth: 300,
      renderCell: (params) => (
        <div className="">{params.value ? params.value : "N/A"}</div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },
    {
      field: "duration",
      headerName: "Course Duration",
      cellClassName: "capitalize",
      minWidth: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },
    {
      field: "exam_type",
      headerName: "Course exam",
      cellClassName: "capitalize",
      minWidth: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },
    {
      field: "status",
      headerName: "Status",
      cellClassName: "capitalize",
      width: 150,
      renderCell: (params) => (
        <div
          className={`cellWithStatus ${params.value ? "active" : "inactive"}`}
        >
          {params.value ? "Active" : "Inactive"}
        </div>
      ),
      headerClassName:
        "bg-primary text-white font-bold flex flex-row items-center ",
    },
  ];
  return (
    <Layout PageId={9}>
      <div className="flex flex-col items-center justify-start w-full h-full">
        <div
          id="actions"
          className="flex flex-col md:flex-row items-center justify-evenly w-full gap-x-2 h-auto"
        >
          {/* <div className="flex flex-row items-center justify-center md:w-1/4 w-full ">
            <SearchInput
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search"
            />
          </div> */}
          <div className="flex md:justify-end md:space-x-2 justify-between items-center w-full ">
            {permissions?.Add && (
              <AddItemButton
                label="Add new"
                size="small"
                onClick={() => navigate("/courses/manage/form/create")}
              />
            )}
            {/* <ActionButtonsWithDropdown /> */}
          </div>
        </div>
        <div className="flex flex-col items-start justify-start w-full mt-2">
          <div className="w-full">
            <Box
              sx={{
                "& .MuiDataGrid-root": {
                  border: "none",
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                },

                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: `${theme.palette.primary.main}`,
                  borderBottom: "none",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: "#fff !important",
                },

                "& .MuiDataGrid-colCell": {
                  color: `#fff !important`,
                },

                "& .MuiDataGrid-footerContainer": {
                  display: "none",
                },
              }}
            >
              <DataGrid
                rows={rows}
                columns={coloumns}
                getRowId={(row) => row.course_id}
                rowHeight={60}
                autoHeight={true}
                loading={loading}
              />
            </Box>
          </div>
        </div>

        {/* <PaginationButtons
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        /> */}
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Confirm Deletion"
          message="Are you sure you want to delete this data?"
        />
      </div>
    </Layout>
  );
};

export default Course;
